import React, { useCallback, useEffect, useState } from 'react';
import { Calendar, RefreshCw, Sword, Timer, Target } from 'lucide-react';
import { cn } from '../../lib/utils';
import { gamesAdminApi, type GameLimits } from '../../api/games';
import { Card, NumberField, SaveButton, TabIntro } from './controls';

/**
 * Вкладка «Лимиты» — дневные нормы бесплатного доступа к играм.
 * Раньше это были константы в коде, любая правка баланса требовала деплоя.
 */
export default function LimitsTab({ isDark }: { isDark: boolean }) {
  const [limits, setLimits] = useState<GameLimits | null>(null);
  const [draft, setDraft] = useState<GameLimits | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await gamesAdminApi.gameLimits();
      setLimits(data);
      setDraft(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Не удалось загрузить');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      // Шлём только редактируемые поля: в draft лежит и то, что пришло из GET
      // (id, updated_at), а серверу это не нужно.
      const next = await gamesAdminApi.updateGameLimits({
        duel_free_daily: draft.duel_free_daily,
        time_attack_free_daily: draft.time_attack_free_daily,
        survival_free_daily: draft.survival_free_daily,
        daily_freeze_per_week: draft.daily_freeze_per_week,
        daily_replay_window_days: draft.daily_replay_window_days,
      });
      setLimits(next);
      setDraft(next);
      setSavedAt(Date.now());
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  const dirty =
    !!draft && !!limits && JSON.stringify(draft) !== JSON.stringify(limits);

  const set = (patch: Partial<GameLimits>) =>
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));

  return (
    <div className="space-y-6">
      <TabIntro isDark={isDark}>
        Сколько раз в сутки бесплатный игрок может сыграть в каждую игру. У
        премиума попыток нет вовсе — эти числа на него не влияют. Счётчик
        тратится в момент старта игры, а не по её завершении, и обнуляется в
        полночь по UTC (05:00 по Ташкенту). Правки доезжают до сервера за
        полминуты, деплой не нужен.
      </TabIntro>

      <div className="flex items-start justify-end gap-4">
        <button
          onClick={load}
          className={cn(
            'flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition shrink-0',
            isDark
              ? 'border-slate-700 text-slate-300 hover:bg-slate-800'
              : 'border-slate-200 text-slate-700 hover:bg-slate-100',
          )}
        >
          <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
          Обновить
        </button>
      </div>

      {error && (
        <div className="rounded-xl px-4 py-3 text-sm bg-red-500/10 text-red-500 border border-red-500/30">
          {error}
        </div>
      )}

      {draft && (
        <>
          <Card
            isDark={isDark}
            title="Попыток в сутки для free"
            icon={<Sword className="w-5 h-5" />}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <NumberField
                isDark={isDark}
                label="Дуэль"
                hint="Попытка списывается при поиске соперника"
                value={draft.duel_free_daily}
                min={0}
                max={100}
                onChange={(duel_free_daily) => set({ duel_free_daily })}
              />
              <NumberField
                isDark={isDark}
                label="Тайм-атака"
                value={draft.time_attack_free_daily}
                min={0}
                max={100}
                onChange={(time_attack_free_daily) =>
                  set({ time_attack_free_daily })
                }
              />
              <NumberField
                isDark={isDark}
                label="Выживание"
                value={draft.survival_free_daily}
                min={0}
                max={100}
                onChange={(survival_free_daily) => set({ survival_free_daily })}
              />
            </div>
            <p
              className={cn(
                'text-xs mt-3',
                isDark ? 'text-slate-500' : 'text-slate-400',
              )}
            >
              Ноль закрывает игру для бесплатных полностью. Счётчики обнуляются в
              полночь по UTC — это 05:00 по Ташкенту.
            </p>
          </Card>

          <Card
            isDark={isDark}
            title="Вызов дня"
            icon={<Calendar className="w-5 h-5" />}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <NumberField
                isDark={isDark}
                label="Заморозок стрика в неделю"
                hint="Только для премиума"
                value={draft.daily_freeze_per_week}
                min={0}
                max={7}
                onChange={(daily_freeze_per_week) =>
                  set({ daily_freeze_per_week })
                }
              />
              <NumberField
                isDark={isDark}
                label="Окно доигрывания, дней"
                hint="Насколько глубоко премиум может закрыть пропущенные дни"
                value={draft.daily_replay_window_days}
                min={1}
                max={365}
                onChange={(daily_replay_window_days) =>
                  set({ daily_replay_window_days })
                }
              />
            </div>
          </Card>

          <div className="flex items-center gap-3">
            <SaveButton isDark={isDark} saving={saving} onClick={save} />
            {dirty && !saving && (
              <span className="text-xs text-amber-500">Есть несохранённые правки</span>
            )}
            {!dirty && savedAt && (
              <span className="text-xs text-emerald-500">Сохранено</span>
            )}
          </div>

          <Card
            isDark={isDark}
            title="Что остаётся только у премиума"
            icon={<Target className="w-5 h-5" />}
          >
            <ul
              className={cn(
                'text-sm space-y-2 list-disc pl-5',
                isDark ? 'text-slate-300' : 'text-slate-600',
              )}
            >
              <li>Дуэль по выбранной теме</li>
              <li>Второй шанс после ошибки в «Выживании»</li>
              <li>Доигрывание пропущенных дней и заморозка стрика</li>
              <li>Безлимитные попытки во всех играх</li>
            </ul>
            <p
              className={cn(
                'text-xs mt-3',
                isDark ? 'text-slate-500' : 'text-slate-400',
              )}
            >
              Это граница подписки, а не настройка — меняется только в коде.
            </p>
          </Card>
        </>
      )}

      {loading && !draft && (
        <div className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
          <Timer className="w-4 h-4 inline mr-2" />
          Загружаю…
        </div>
      )}
    </div>
  );
}
